import {
  EnterpriseAutocompletedField,
  EnterpriseAutocompletedFieldId,
  EnterpriseAutocompletedProfilePatchDto
} from 'src/api/enterprise-profile/types';
import { OnBoardingEnterpriseProfile } from 'src/pages/onboarding/create-profile/enterprise/types';
import { DraftShortlist } from '../api/draft-shortlist/types';
import { JobSkill } from '../api/taxonomies/job-skills-api';
import { getExperienceCode } from '../pages/onboarding/create-profile/person/types';
import { Experience } from '../api/person-profile/types';

const fieldLabels: Record<EnterpriseAutocompletedFieldId, string> = {
  // Enterprise
  [EnterpriseAutocompletedFieldId.Name]: 'Nombre de la empresa',
  [EnterpriseAutocompletedFieldId.Description]: 'Descripción',
  [EnterpriseAutocompletedFieldId.WebsiteUrl]: 'Sitio web',
  [EnterpriseAutocompletedFieldId.EnterpriseSize]: 'Tamaño de la empresa',
  [EnterpriseAutocompletedFieldId.IndustryAreaId]: 'Área de la industria',
  [EnterpriseAutocompletedFieldId.ProfileImageFileId]: 'Imagen de perfil',
  [EnterpriseAutocompletedFieldId.BannerImageFileId]: 'Imagen de fondo',

  // Admin
  [EnterpriseAutocompletedFieldId.AdminName]: 'Nombre del administrador',
  [EnterpriseAutocompletedFieldId.AdminProfileImageId]: 'Imagen de perfil del administrador',
  [EnterpriseAutocompletedFieldId.AdminEmail]: 'Email del administrador',
  [EnterpriseAutocompletedFieldId.AdminPhoneNumber]: 'Teléfono del administrador',

  // Project
  [EnterpriseAutocompletedFieldId.ProjectName]: 'Nombre del proyecto',
  [EnterpriseAutocompletedFieldId.ProjectObjectives]: 'Objetivos del proyecto',
  [EnterpriseAutocompletedFieldId.ProjectManagerName]: 'Nombre del jefe de proyecto',

  // Service Request
  [EnterpriseAutocompletedFieldId.ServiceRequestRole]: 'Rol solicitado',
  [EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience]: 'Experiencia requerida',
  [EnterpriseAutocompletedFieldId.ServiceRequestSkills]: 'Habilidades requeridas',
  [EnterpriseAutocompletedFieldId.ServiceRequestLocation]: 'Ubicación del trabajo',
  [EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference]: 'Preferencia de modalidad de trabajo',
  [EnterpriseAutocompletedFieldId.ServiceRequestDescription]: 'Descripción del puesto'
};

export function extractEnterpriseAutocompletedFields(dto: Partial<EnterpriseAutocompletedProfilePatchDto>): EnterpriseAutocompletedField[] {
  const fields: EnterpriseAutocompletedField[] = [];

  // Enterprise
  if (dto.name) {
    fields.push({ id: EnterpriseAutocompletedFieldId.Name, label: fieldLabels[EnterpriseAutocompletedFieldId.Name], value: dto.name });
  }
  if (dto.description) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.Description,
      label: fieldLabels[EnterpriseAutocompletedFieldId.Description],
      value: dto.description
    });
  }
  if (dto.websiteUrl) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.WebsiteUrl,
      label: fieldLabels[EnterpriseAutocompletedFieldId.WebsiteUrl],
      value: dto.websiteUrl
    });
  }
  if (dto.enterpriseSize) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.EnterpriseSize,
      label: fieldLabels[EnterpriseAutocompletedFieldId.EnterpriseSize],
      value: dto.enterpriseSize
    });
  }
  if (dto.industryAreaId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.IndustryAreaId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.IndustryAreaId],
      value: dto.industryAreaId
    });
  }
  if (dto.profileImageFileId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProfileImageFileId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProfileImageFileId],
      value: dto.profileImageFileId
    });
  }
  if (dto.bannerImageFileId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.BannerImageFileId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.BannerImageFileId],
      value: dto.bannerImageFileId
    });
  }

  // Admin
  if (dto.adminName) {
    fields.push({ id: EnterpriseAutocompletedFieldId.AdminName, label: fieldLabels[EnterpriseAutocompletedFieldId.AdminName], value: dto.adminName });
  }
  if (dto.adminProfileImageFileId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.AdminProfileImageId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.AdminProfileImageId],
      value: dto.adminProfileImageFileId
    });
  }
  if (dto.adminEmail) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.AdminEmail,
      label: fieldLabels[EnterpriseAutocompletedFieldId.AdminEmail],
      value: dto.adminEmail
    });
  }
  if (dto.adminPhoneNumber) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.AdminPhoneNumber,
      label: fieldLabels[EnterpriseAutocompletedFieldId.AdminPhoneNumber],
      value: dto.adminPhoneNumber
    });
  }

  // Project
  if (dto.projectName) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProjectName,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProjectName],
      value: dto.projectName
    });
  }
  if (dto.projectObjectives) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProjectObjectives,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProjectObjectives],
      value: dto.projectObjectives
    });
  }
  if (dto.projectManagerName) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProjectManagerName,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProjectManagerName],
      value: dto.projectManagerName
    });
  }

  // Service Request
  if (dto.serviceRequestRole) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestRole,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestRole],
      value: dto.serviceRequestRole
    });
  }
  if (dto.serviceRequestRoleExperience) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience],
      value: dto.serviceRequestRoleExperience
    });
  }
  if (dto.serviceRequestSkills?.length) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestSkills,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestSkills],
      value: dto.serviceRequestSkills
    });
  }
  if (dto.serviceRequestLocation) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestLocation,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestLocation],
      value: dto.serviceRequestLocation
    });
  }
  if (dto.serviceRequestLocationWorkPreference) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference],
      value: dto.serviceRequestLocationWorkPreference
    });
  }

  return fields;
}

type EnterpriseFieldMapper = (value: any, existing: OnBoardingEnterpriseProfile, result: Partial<OnBoardingEnterpriseProfile>) => void;

const ENTERPRISE_FIELD_MAPPERS: Partial<Record<EnterpriseAutocompletedFieldId, EnterpriseFieldMapper>> = {
  // Enterprise
  [EnterpriseAutocompletedFieldId.Name]: (value, existing, result) => {
    if (!existing.enterpriseName) result.enterpriseName = value;
  },
  [EnterpriseAutocompletedFieldId.Description]: (value, existing, result) => {
    if (!existing.enterpriseDescription) result.enterpriseDescription = value;
  },
  [EnterpriseAutocompletedFieldId.WebsiteUrl]: (value, existing, result) => {
    if (!existing.websiteUrl) result.websiteUrl = value;
  },
  [EnterpriseAutocompletedFieldId.ProfileImageFileId]: (value, existing, result) => {
    if (!existing.enterpriseImgFileId) result.enterpriseImgFileId = value;
  },
  [EnterpriseAutocompletedFieldId.IndustryAreaId]: (value, existing, result) => {
    if (!existing.industryAreaId) result.industryAreaId = value;
  },

  // Admin
  [EnterpriseAutocompletedFieldId.AdminName]: (value, existing, result) => {
    if (!existing.adminFirstName) result.adminFirstName = value.firstName;
    if (!existing.adminLastName) result.adminLastName = value.lastName;
  },

  [EnterpriseAutocompletedFieldId.AdminProfileImageId]: (value, existing, result) => {
    if (!existing.adminImgFileId) result.adminImgFileId = value;
  },

  [EnterpriseAutocompletedFieldId.AdminPhoneNumber]: (value, existing, result) => {
    if (!existing.phoneNumber) {
      result.phoneNumber = value.number;
      result.phoneNumberCountryId = value.countryId;
    }
  },

  // Project
  [EnterpriseAutocompletedFieldId.ProjectName]: (value, existing, result) => {
    if (!existing.project?.projectInfo?.name) {
      result.project ??= {} as any;
      result.project!.projectInfo ??= {} as any;
      result.project!.projectInfo.name = value;
    }
  },

  [EnterpriseAutocompletedFieldId.ProjectObjectives]: (value, existing, result) => {
    if (!existing.project?.projectInfo?.objectives) {
      result.project ??= {} as any;
      result.project!.projectInfo ??= {} as any;
      result.project!.projectInfo.objectives = value;
    }
  },

  [EnterpriseAutocompletedFieldId.ProjectManagerName]: (value, existing, result) => {
    if (!existing.project?.projectInfo?.projectManagerName) {
      result.project ??= {} as any;
      result.project!.projectInfo ??= {} as any;
      result.project!.projectInfo.projectManagerName = value;
    }
  },

  // Service Request
  [EnterpriseAutocompletedFieldId.ServiceRequestDescription]: (value, existing, result) => {
    if (!existing.project?.requests?.[0]?.description) {
      result.project ??= {} as any;
      result.project!.requests ??= [{} as any];
      result.project!.requests[0].description = value;
    }
  },

  [EnterpriseAutocompletedFieldId.ServiceRequestRole]: (value, existing, result) => {
    if (!existing.project?.requests?.[0]?.role) {
      result.project ??= {} as any;
      result.project!.requests ??= [{} as any];
      result.project!.requests[0].role = value;
    }
  },

  [EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience]: (value: Experience, existing, result) => {
    if (!existing.project?.requests?.[0]?.roleExperience) {
      result.project ??= {} as any;
      result.project!.requests ??= [{} as any];
      result.project!.requests[0].roleExperience = {
        ge: value.yearsGreatEqThan,
        le: value.yearsLessEqThan,
        code: getExperienceCode(value) ?? ''
      };
    }
  },

  [EnterpriseAutocompletedFieldId.ServiceRequestSkills]: (value: JobSkill[], existing, result) => {
    if (!existing.project?.requests?.[0]?.skills?.length) {
      result.project ??= {} as any;
      result.project!.requests ??= [{} as any];
      result.project!.requests[0].skills = value.map((skill) => ({ id: skill.id, name: skill.name }));
    }
  },

  [EnterpriseAutocompletedFieldId.ServiceRequestLocation]: (value, existing, result) => {
    if (!existing.project?.requests?.[0]?.location) {
      result.project ??= {} as any;
      result.project!.requests ??= [{} as any];
      result.project!.requests[0].location = value;
    }
  },

  [EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference]: (value, existing, result) => {
    if (!existing.project?.requests?.[0]?.locationWorkPreference) {
      result.project ??= {} as any;
      result.project!.requests ??= [{} as any];
      result.project!.requests[0].locationWorkPreference = value;
    }
  }
};

export function mapEnterpriseAutocompletedFieldsToProfile(
  fields: EnterpriseAutocompletedField[],
  existingProfile: OnBoardingEnterpriseProfile
): Partial<OnBoardingEnterpriseProfile> {
  const result: Partial<OnBoardingEnterpriseProfile> = {};

  for (const { id, value } of fields) {
    const fn = ENTERPRISE_FIELD_MAPPERS[id];
    if (fn) fn(value, existingProfile, result);
  }

  return result;
}

/**
 * Currently unreachable, and left in place deliberately.
 *
 * Nothing imports it, and since j2l-website#1893 nothing in this SPA can even build a `DraftShortlist` to
 * pass it: the only draft-shortlist read left is `getDraftShortlist`, which returns the flatter
 * `DraftShortlistDto` from `{id}/public`, and the OData reader that produced this shape was removed because
 * its `$expand` reached the draft enterprise admin's personal data on a route that is now back-office only.
 *
 * It is kept rather than deleted because it is a pure mapping function with no request in it, so it leaks
 * nothing on its own, and because deleting it means deleting the whole `DraftShortlist` type tree with it,
 * which is a frontend change that wants its own review. What it must not become is a reason to re-add an
 * OData reader for `draftShortlists`: that route answers 403 for every role this SPA can hold.
 */
export function extractDraftShortlistAutocompletedFields(draft: DraftShortlist | undefined): EnterpriseAutocompletedField[] {
  if (!draft) return [];

  const enterprise = draft.hiringNeed?.project?.enterpriseProfile;
  const admin = enterprise?.admin;
  const project = draft.hiringNeed?.project;
  const hiringNeed = draft.hiringNeed;

  const fields: EnterpriseAutocompletedField[] = [];

  // Enterprise
  if (enterprise?.name) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.Name,
      label: fieldLabels[EnterpriseAutocompletedFieldId.Name],
      value: enterprise.name
    });
  }

  if (enterprise?.industryAreaId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.IndustryAreaId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.IndustryAreaId],
      value: enterprise.industryAreaId
    });
  }

  if (enterprise?.logoFileId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProfileImageFileId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProfileImageFileId],
      value: enterprise.logoFileId
    });
  }

  // Admin
  if (admin?.fullName) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.AdminName,
      label: fieldLabels[EnterpriseAutocompletedFieldId.AdminName],
      value: admin.fullName
    });
  }

  if (admin?.profileImageFileId) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.AdminProfileImageId,
      label: fieldLabels[EnterpriseAutocompletedFieldId.AdminProfileImageId],
      value: admin.profileImageFileId
    });
  }

  // Project
  if (project?.name) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProjectName,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProjectName],
      value: project.name
    });
  }

  if (project?.objectives) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProjectObjectives,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProjectObjectives],
      value: project.objectives
    });
  }

  if (project?.projectManagerName) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ProjectManagerName,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ProjectManagerName],
      value: project.projectManagerName
    });
  }

  // Service Request (HiringNeed)
  if (hiringNeed?.role) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestRole,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestRole],
      value: hiringNeed.role
    });
  }

  if (hiringNeed?.experience) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestRoleExperience],
      value: hiringNeed.experience
    });
  }

  if (hiringNeed?.skills?.length) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestSkills,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestSkills],
      value: hiringNeed.skills.map((skill) => skill.skill)
    });
  }

  if (hiringNeed?.location) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestLocation,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestLocation],
      value: hiringNeed.location
    });
  }

  if (hiringNeed?.locationWorkPreference) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestLocationWorkPreference],
      value: hiringNeed.locationWorkPreference
    });
  }

  if (hiringNeed?.description) {
    fields.push({
      id: EnterpriseAutocompletedFieldId.ServiceRequestDescription,
      label: fieldLabels[EnterpriseAutocompletedFieldId.ServiceRequestDescription],
      value: hiringNeed.description
    });
  }

  return fields;
}
